import type { BatteryChemistry } from '@brt/shared-types'

export interface CycleLifeAssessment {
  lifePositionPercent: number | null
  rangeMin: number | null
  rangeMax: number | null
  sohPlausible: boolean
  note: string
}

const NUMBER_PATTERN = /^\d+(?:\.\d+)?$/

export function parseCycleLifeRange(range: string): { min: number | null; max: number | null } {
  if (!range || range.trim() === '') return { min: null, max: null }

  const trimmed = range.trim()

  // Try separators: en dash (–), em dash (—), hyphen (-)
  // Use a regex that captures two numbers around any dash
  const rangeMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*[–—-]\s*(\d+(?:\.\d+)?)$/)
  if (rangeMatch) {
    const lo = parseFloat(rangeMatch[1]!)
    const hi = parseFloat(rangeMatch[2]!)
    return { min: lo, max: hi }
  }

  if (NUMBER_PATTERN.test(trimmed)) {
    const v = parseFloat(trimmed)
    return { min: v, max: v }
  }

  return { min: null, max: null }
}

export function assessCycleLifePosition(
  ageCycles: number,
  cycleLifeRange: string,
  stateOfHealth: number,
): CycleLifeAssessment {
  const { min, max } = parseCycleLifeRange(cycleLifeRange)

  if (max === null || max <= 0) {
    return {
      lifePositionPercent: null,
      rangeMin: min,
      rangeMax: max,
      sohPlausible: true,
      note: 'No cycle-life data available for this chemistry',
    }
  }

  const lifePositionPercent = (ageCycles / max) * 100

  // Expected SoH floor at this cycle position: linear from 100% at 0 cycles to 80% at rangeMax
  const expectedSohFloor = 1.0 - 0.2 * Math.min(1, ageCycles / max)
  const tolerance = 0.05
  const sohPlausible = stateOfHealth >= expectedSohFloor - tolerance

  let note: string
  if (sohPlausible) {
    note = `At ${ageCycles} cycles, expected SoH ≥ ${(expectedSohFloor * 100).toFixed(0)}%`
  } else {
    note = `SoH ${((stateOfHealth) * 100).toFixed(0)}% is below expected ${(expectedSohFloor * 100).toFixed(0)}% for ${ageCycles} cycles — capacity loss faster than typical`
  }

  return {
    lifePositionPercent,
    rangeMin: min,
    rangeMax: max,
    sohPlausible,
    note,
  }
}

// Re-export to surface all chemistries for callers who want to enumerate presets
export type { BatteryChemistry }
